import { KeyboardEvent, ReactNode } from 'react';
import { Stack, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useClipboard from 'src/@core/hooks/useClipboard';
import { VigoTooltip } from 'src/@vigo/vigo-tooltip';

const COPY_FEEDBACK_DURATION_MS = 500;

interface VigoCopyableTextProps {
  value: string;
  children: ReactNode;
}

const VigoCopyableText = ({ value, children }: VigoCopyableTextProps) => {
  const { copy, copied } = useClipboard({ copiedTimeout: COPY_FEEDBACK_DURATION_MS });
  const theme = useTheme();

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      copy(value);
    }
  };

  if (!value?.trim()) return <>{children}</>;

  return (
    <VigoTooltip title={copied ? 'Copied' : 'Copy'}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        role="button"
        tabIndex={0}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        onClick={() => copy(value)}
        onKeyDown={handleKeyDown}
        sx={{ cursor: 'pointer', '&:hover, &:focus-visible': { opacity: 0.75 } }}>
        {children}
        <ContentCopyIcon
          fontSize="small"
          sx={{ color: copied ? theme.palette.customColors.primaryGradient : 'inherit' }}
        />
      </Stack>
    </VigoTooltip>
  );
};

export default VigoCopyableText;
